import React, { useState, useEffect, useCallback, useMemo } from "react";
import './EditShop.css';
import { Link, useOutletContext } from "react-router-dom";
import Select from 'react-select';

const API_URL = process.env.REACT_APP_API_URL || 'https://pyate-backend-production-f7ce.up.railway.app';

// ── LocalStorage cache helpers ────────────────────────────────────────────────
const CACHE_TTL = 300_000; // 5 minutes

function lsGet(key) {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const { val, exp } = JSON.parse(raw);
        if (Date.now() > exp) { localStorage.removeItem(key); return null; }
        return val;
    } catch { return null; }
}

function lsSet(key, val, ttl = CACHE_TTL) {
    try { localStorage.setItem(key, JSON.stringify({ val, exp: Date.now() + ttl })); } catch {}
}

function lsDel(key) {
    try { localStorage.removeItem(key); } catch {}
}

function normalizeCategoryName(value = "") {
    return value.toLowerCase().trim().replace(/[^a-z0-9]/g, "");
}

function getCategoryKeyword(value = "") {
    const normalized = normalizeCategoryName(value);
    return normalized.replace(/(ing|ed|es|s)$/i, "");
}

function areCategoriesSimilar(existing = "", incoming = "") {
    const a = normalizeCategoryName(existing);
    const b = normalizeCategoryName(incoming);
    if (!a || !b) return false;
    if (a === b) return true;
    if ((a.length >= 2 && b.includes(a)) || (b.length >= 2 && a.includes(b))) return true;

    const aKeyword = getCategoryKeyword(a);
    const bKeyword = getCategoryKeyword(b);
    if (!aKeyword || !bKeyword) return false;
    if (aKeyword.length < 2 || bKeyword.length < 2) return false;

    return (
        aKeyword === bKeyword ||
        aKeyword.includes(bKeyword) ||
        bKeyword.includes(aKeyword)
    );
}

function EditShop() {
    const { userId, pageId, token, shopItems: prefetchedItems, shopCats: prefetchedCats } = useOutletContext();
    const responsive = {
        superLargeDesktop: {
            breakpoint: { max: 4000, min: 1024 },
            items: 5,
        },
        desktop: {
            breakpoint: { max: 1024, min: 800 },
            items: 4,
        },
        tablet: {
            breakpoint: { max: 800, min: 464 },
            items: 2,
        },
        mobile: {
            breakpoint: { max: 464, min: 0 },
            items: 1,
        }
    };

    const [cards, setCards] = useState([]);
    const [cart, setCart] = useState(() => {
        const storedCart = JSON.parse(localStorage.getItem("cart")) || [];
        return storedCart;
    });
    const [newCard, setNewCard] = useState({
        name: "",
        description: "",
        price: "",
        mrp: "",
        discount: "",
        quantity: "",
        image: "",
        pdfs: [],
        category: null,
    });
    const [showForm, setShowForm] = useState(false);
    const [popupMessage, setPopupMessage] = useState("");
    const [loading, setLoading] = useState(true);
    const [categories, setCategories] = useState([]);
    const [newCategory, setNewCategory] = useState("");
    const [addingNewCategory, setAddingNewCategory] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const API_URL = process.env.REACT_APP_API_URL || 'https://pyate-backend-production-9749.up.railway.app';

    const [editingCard, setEditingCard] = useState(null); // card being edited
    const [editForm, setEditForm] = useState({
        name: "", description: "", price: "", mrp: "",
        discount: "", quantity: "", image: "", category: null,
    });
    const [isEditSaving, setIsEditSaving] = useState(false);

    useEffect(() => {
        if (!userId || !pageId) return;

        // ── Fast path: EditStore already prefetched this data ─────────────
        // Only use when data is actually an array (prefetch complete).
        // null means EditStore hasn't finished yet — fall through to own fetch.
        if (Array.isArray(prefetchedItems) && Array.isArray(prefetchedCats)) {
            setCards(prefetchedItems);
            setCategories(prefetchedCats);
            setLoading(false);
            return;
        }

        // ── Fallback: fetch directly if prefetch not ready yet ────────────
        const controller = new AbortController();
        const { signal } = controller;

        const fetchAll = async () => {
            const itemsCacheKey = `editshop_items:${userId}:${pageId}`;
            const catsCacheKey  = `editshop_cats`;

            // ── Serve from cache immediately — no spinner on re-navigation ──
            const cachedItems = lsGet(itemsCacheKey);
            const cachedCats  = lsGet(catsCacheKey);
            if (cachedItems) { setCards(cachedItems); setLoading(false); }
            if (cachedCats)  { setCategories(cachedCats); }

            // If both are cached, skip the network entirely
            if (cachedItems && cachedCats) return;

            // Only show spinner if we have no cached items yet
            if (!cachedItems) setLoading(true);

            try {
                const t0 = performance.now();

                const fetches = [];
                if (!cachedItems) fetches.push(
                    fetch(`${API_URL}/api/get_edit_items?userid=${userId}&pageid=${pageId}`, { signal })
                );
                if (!cachedCats) fetches.push(
                    fetch(`${API_URL}/api/categories/editshop`, { signal })
                );

                const results = await Promise.all(fetches);
                const elapsed = performance.now() - t0;
                if (elapsed > 2000) {
                    console.error(`[SLOW] EditShop fetch took ${(elapsed / 1000).toFixed(2)}s (>2s). `
                        + `Check /api/get_edit_items and /api/categories/editshop response times.`);
                }

                let ri = 0;
                if (!cachedItems) {
                    const res = results[ri++];
                    if (res.ok) {
                        const data = await res.json();
                        setCards(data);
                        lsSet(itemsCacheKey, data);
                    } else {
                        console.error(`[EditShop] get_edit_items → ${res.status}`);
                    }
                }
                if (!cachedCats) {
                    const res = results[ri++];
                    if (res.ok) {
                        const data = await res.json();
                        const opts = (data.categories || []).map(c => ({ value: c, label: c }));
                        setCategories(opts);
                        lsSet(catsCacheKey, opts, 5 * 60_000); // categories cache 5 min
                    } else {
                        console.error(`[EditShop] categories/editshop → ${res.status}`);
                    }
                }
            } catch (error) {
                if (error.name === 'AbortError') return;
                console.error('[EditShop] fetchAll error:', error);
            } finally {
                if (!signal.aborted) setLoading(false);
            }
        };

        fetchAll();
        return () => controller.abort();
    }, [userId, pageId, prefetchedItems, prefetchedCats]);

    // Calculate discount automatically when price or MRP changes (add form)
    useEffect(() => {
        if (newCard.price && newCard.mrp) {
            const price = parseFloat(newCard.price);
            const mrp = parseFloat(newCard.mrp);
            if (mrp > 0 && price <= mrp) {
                const discount = Math.round(((mrp - price) / mrp) * 100);
                setNewCard(prev => ({
                    ...prev,
                    discount: discount.toString()
                }));
            }
        }
    }, [newCard.price, newCard.mrp]);

    // Calculate discount automatically for edit form
    useEffect(() => {
        if (editForm.price && editForm.mrp) {
            const price = parseFloat(editForm.price);
            const mrp = parseFloat(editForm.mrp);
            if (mrp > 0 && price <= mrp) {
                const discount = Math.round(((mrp - price) / mrp) * 100);
                setEditForm(prev => ({ ...prev, discount: discount.toString() }));
            }
        }
    }, [editForm.price, editForm.mrp]);

    const addToCart = (item) => {
        const updatedCart = [...cart, item];
        setCart(updatedCart);
        localStorage.setItem("cart", JSON.stringify(updatedCart));
        alert(`${item.name} has been added to the cart!`);
    };

    const handleInputChange = useCallback((e) => {
        const { name, value } = e.target;
        setNewCard(prev => ({ ...prev, [name]: value }));
    }, []);

    const handleImageChange = useCallback((e) => {
        const file = e.target.files[0];
        if (file) setNewCard(prev => ({ ...prev, image: file }));
    }, []);

    const handlePdfChange = useCallback((e) => {
        const files = Array.from(e.target.files || []);
        setNewCard(prev => ({ ...prev, pdfs: files }));
    }, []);

    const addCard = useCallback(async () => {
        if (isSaving) return;
        setIsSaving(true);

        // Required fields check
        if (
            !newCard.name ||
            !newCard.price ||
            !newCard.mrp ||
            !newCard.discount ||
            !newCard.quantity ||
            !userId ||
            !pageId ||
            !(newCard.category && newCard.category.value) ||
            !(newCard.image instanceof File)
        ) {
            alert("Please fill all fields and select an image.");
            setIsSaving(false);
            return;
        }

        try {
            const formData = new FormData();
            formData.append('name', newCard.name);
            formData.append('description', newCard.description);
            formData.append('price', newCard.price);
            formData.append('mrp', newCard.mrp);
            formData.append('discount', newCard.discount);
            formData.append('quantity', newCard.quantity);
            formData.append('userid', userId);
            formData.append('pageid', pageId);
            formData.append('category', newCard.category ? newCard.category.value : "");
            formData.append('image', newCard.image);

            if (Array.isArray(newCard.pdfs) && newCard.pdfs.length > 0) {
                newCard.pdfs.forEach((file) => {
                    formData.append('pdfs', file);
                });
            }

            const response = await fetch(`${API_URL}/api/shop_items`, {
                method: 'POST',
                body: formData,
            });

            if (response.ok) {
                const data = await response.json();
                const addedCard = data.item;
                setCards(prev => [...prev, addedCard]);
                // Bust cache so next re-navigation fetches fresh data
                lsDel(`editshop_items:${userId}:${pageId}`);
                setNewCard({
                    name: "", description: "", price: "", mrp: "",
                    discount: "", quantity: "", image: "", pdfs: [], category: null,
                });
                setShowForm(false);
            } else {
                const err = await response.json();
                console.error("Error adding card:", err);
                alert(err.error || "Error adding item.");
            }
        } catch (error) {
            console.error("Error adding card:", error);
            alert("Error adding item.");
        } finally {
            setIsSaving(false);
        }
    }, [isSaving, newCard, userId, pageId]);

    const openEditModal = useCallback((card) => {
        setEditingCard(card);
        setEditForm({
            name: card.name || "",
            description: card.description || "",
            price: card.price || "",
            mrp: card.mrp || "",
            discount: card.discount || "",
            quantity: card.quantity || "",
            image: "", // new file optional
            category: card.category ? { value: card.category, label: card.category } : null,
        });
    }, []);

    const handleEditInputChange = useCallback((e) => {
        const { name, value } = e.target;
        setEditForm(prev => ({ ...prev, [name]: value }));
    }, []);

    const handleEditImageChange = useCallback((e) => {
        const file = e.target.files[0];
        if (file) setEditForm(prev => ({ ...prev, image: file }));
    }, []);

    const handleEditCategoryChange = useCallback((selectedOption) => {
        setEditForm(prev => ({ ...prev, category: selectedOption }));
    }, []);

    const saveEdit = useCallback(async () => {
        if (isEditSaving || !editingCard) return;
        if (!editForm.name || !editForm.price || !editForm.mrp || !editForm.quantity) {
            alert("Please fill all required fields.");
            return;
        }
        setIsEditSaving(true);
        try {
            const formData = new FormData();
            formData.append('name', editForm.name);
            formData.append('description', editForm.description);
            formData.append('price', editForm.price);
            formData.append('mrp', editForm.mrp);
            formData.append('discount', editForm.discount);
            formData.append('quantity', editForm.quantity);
            formData.append('category', editForm.category ? editForm.category.value : "");
            formData.append('userid', userId);
            if (editForm.image instanceof File) {
                formData.append('image', editForm.image);
            }

            const response = await fetch(`${API_URL}/api/shop_items/${editingCard.id}`, {
                method: 'PUT',
                body: formData,
            });

            if (response.ok) {
                const data = await response.json();
                const updatedItem = data.item;
                setCards(prev => prev.map(c => c.id === editingCard.id ? updatedItem : c));
                lsDel(`editshop_items:${userId}:${pageId}`);
                setEditingCard(null);
                setPopupMessage('Item updated successfully!');
                setTimeout(() => setPopupMessage(""), 2500);
            } else {
                const err = await response.json();
                alert(err.error || "Error updating item.");
            }
        } catch (error) {
            console.error("Error updating card:", error);
            alert("Error updating item.");
        } finally {
            setIsEditSaving(false);
        }
    }, [isEditSaving, editingCard, editForm, userId, pageId]);

    const deleteCard = useCallback(async (id) => {
        try {
            const response = await fetch(`${API_URL}/api/shop_items/${id}?userid=${userId}`, {
                method: 'DELETE',
            });

            if (response.ok) {
                setCards(prev => prev.filter(card => card.id !== id));
                // Bust cache so re-navigation shows correct list
                lsDel(`editshop_items:${userId}:${pageId}`);
                setPopupMessage('Item deleted successfully!');
                setTimeout(() => setPopupMessage(""), 2000);
            } else if (response.status === 403) {
                alert("You are not authorized to delete this item.");
            } else if (response.status === 404) {
                alert("Item not found.");
            } else {
                console.error("Error deleting card:", await response.json());
                alert("Error deleting item.");
            }
        } catch (error) {
            console.error("Error deleting card:", error);
            alert("Error deleting item.");
        }
    }, [userId, pageId]);

    const handleCategoryChange = useCallback((selectedOption) => {
        setNewCard(prev => ({ ...prev, category: selectedOption }));
    }, []);

    const handleNewCategoryChange = useCallback((e) => {
        setNewCategory(e.target.value);
    }, []);

    const addNewCategory = useCallback(() => {
        setAddingNewCategory(true);
    }, []);

    const saveNewCategory = useCallback(() => {
        const trimmed = newCategory.trim();
        if (trimmed === "") return;

        const matchedCategory = categories.find((cat) =>
            areCategoriesSimilar(cat.value, trimmed)
        );

        if (matchedCategory) {
            setNewCard(prev => ({ ...prev, category: matchedCategory }));
            alert(`Category already exists as "${matchedCategory.label}". Please use the existing category.`);
            setNewCategory("");
            setAddingNewCategory(false);
            return;
        }

        const newOption = { value: trimmed, label: trimmed };
        setCategories(prev => [...prev, newOption]);
        setNewCard(prev => ({ ...prev, category: newOption }));
        setNewCategory("");
        setAddingNewCategory(false);
    }, [newCategory, categories]);

    const suggestedCategory = useMemo(() => {
        const trimmed = newCategory.trim();
        if (!trimmed) return null;
        return categories.find((cat) => areCategoriesSimilar(cat.value, trimmed)) || null;
    }, [newCategory, categories]);

    return (
        <div className="shop-editshop">
            <nav className="sub-navbar-editshop" responsive={responsive}>
    <div className="sub_navcomponent-editshop">
        <ul>
            <li><Link to="/Shop">Shop</Link></li>
        </ul>
    </div>
    <button 
        className="add-card-button-editshop" 
        onClick={() => setShowForm(true)}
        style={{ marginLeft: 'auto' }}  // This pushes the button to the right
    >
        Add Item
    </button>
</nav>

            {loading ? (
                <div className="shop_js-editshop">
                    <style>{`
                        .skel { background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
                                background-size: 200% 100%; animation: skel-shimmer 1.2s infinite;
                                border-radius: 4px; }
                        @keyframes skel-shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
                        .skel-card { background:#fff; border:1px solid #eee; border-radius:8px; overflow:hidden; }
                    `}</style>
                    {[1,2,3,4].map(i => (
                        <div className="shop_card-editshop skel-card" key={i}>
                            <div className="skel" style={{height:160, width:'100%', borderRadius:0}} />
                            <div style={{padding:'10px'}}>
                                <div className="skel" style={{height:13, width:'80%', marginBottom:8}} />
                                <div className="skel" style={{height:13, width:'50%', marginBottom:8}} />
                                <div className="skel" style={{height:18, width:'40%', marginBottom:8}} />
                                <div className="skel" style={{height:13, width:'60%'}} />
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <>
                    <div className="shop_js-editshop" responsive={responsive}>
                        {cards.map((card) => (
                            <div className="shop_card-editshop" key={card.id}>
                                <img src={card.image_url} alt="Avatar" className="card-image-editshop" loading="lazy" />
                                <div className="shop_container-editshop" style={{ textAlign: "justify" }}>
                                    <a href="/ring" style={{ textDecoration: 'none', fontSize: '11pt', color: 'black' }}>{card.name}</a>
                                    <div className="card-text-editshop">
                                        {card.description && <div style={{ fontSize: '9pt', color: 'gray' }}>{card.description}</div>}
                                        <b style={{ fontSize: '12pt', color: 'black' }}>₹{card.price}</b>
                                        <span style={{ color: 'green', padding: '10px' }}>({card.discount}% off)</span>
                                        <span style={{ textDecoration: 'line-through' }}>M.R.P.: ₹{card.mrp}</span><br />
                                        <span>Quantity: {card.quantity}</span><br />
                                        <div className="card-actions-editshop">
                                            <button className="edit-btn-editshop" onClick={() => openEditModal(card)}>Edit</button>
                                            <button className="delete-btn-editshop" onClick={() => deleteCard(card.id)}>Delete</button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                   
                </>
            )}

            {showForm && (
                <div className="form-modal-editshop">
                    <div className="form-content-editshop">
                        <div className="form-scroll-container-editshop">
                            <h3>Add New Item</h3>
                            <div className="form-row-editshop">
                                <label htmlFor="name">Name</label>
                                <input id="name" type="text" name="name" placeholder="Enter Name" value={newCard.name} onChange={handleInputChange} required/>
                            </div>
                            <div className="form-row-editshop">
                                <label htmlFor="description">Description</label>
                                <input id="description" type="text" name="description" placeholder="Enter Description" value={newCard.description} onChange={handleInputChange} required/>
                            </div>
                            <div className="form-row-editshop">
                                <label htmlFor="mrp">MRP</label>
                                <input 
                                    id="mrp" 
                                    type="number" 
                                    name="mrp" 
                                    placeholder="Enter MRP" 
                                    value={newCard.mrp} 
                                    onChange={handleInputChange} 
                                    required
                                />
                            </div>
                            <div className="form-row-editshop">
                                <label htmlFor="price">Selling Price</label>
                                <input 
                                    id="price" 
                                    type="number" 
                                    name="price" 
                                    placeholder="Enter Price" 
                                    value={newCard.price} 
                                    onChange={handleInputChange} 
                                    required
                                />
                            </div>
                            <div className="form-row-editshop">
                                <label htmlFor="discount">Discount (%)</label>
                                <input 
                                    id="discount" 
                                    type="number" 
                                    name="discount" 
                                    placeholder="Discount" 
                                    value={newCard.discount} 
                                    onChange={handleInputChange}
                                    readOnly
                                    className="discount-input-readonly"
                                />
                            </div>
                            <div className="form-row-editshop">
                                <label htmlFor="quantity">Quantity</label>
                                <input id="quantity" type="number" name="quantity" placeholder="Enter Quantity" value={newCard.quantity} onChange={handleInputChange} required/>
                            </div>
                            <div className="form-row-editshop">
                                <label htmlFor="image">Image</label>
                                <input id="image" type="file" name="image" accept="image/*" onChange={handleImageChange} required/>
                            </div>
                            <div className="form-row-editshop">
                                <label htmlFor="pdfs">PDFs (optional)</label>
                                <input
                                    id="pdfs"
                                    type="file"
                                    name="pdfs"
                                    accept=".pdf,application/pdf"
                                    multiple
                                    onChange={handlePdfChange}
                                />
                                {Array.isArray(newCard.pdfs) && newCard.pdfs.length > 0 && (
                                    <div style={{ fontSize: '9pt', color: 'gray', marginTop: '6px' }}>
                                        Selected: {newCard.pdfs.map((f) => f.name).join(", ")}
                                    </div>
                                )}
                            </div>
                            <div className="form-row-editshop">
                                <label htmlFor="category">Category</label>
                                <div className="select-wrapper-editshop">
                                    <Select
                                        inputId="category"
                                        value={newCard.category}
                                        onChange={handleCategoryChange}
                                        options={categories}
                                        isSearchable
                                        placeholder="Select Category"
                                        required
                                    />
                                </div>
                            </div>
                            {!addingNewCategory ? (
                                <button
                                    type="button"
                                    onClick={addNewCategory}
                                    className="add-category-button-editshop"
                                >
                                    Add New Category
                                </button>
                            ) : (
                                <div className="form-row-editshop">
                                    <label htmlFor="newCategory">New Category</label>
                                    <input
                                        id="newCategory"
                                        type="text"
                                        placeholder="Category..."
                                        value={newCategory}
                                        onChange={handleNewCategoryChange}
                                        className="new-category-input-editshop"
                                    />
                                    <button type="button" onClick={saveNewCategory} className="save-category-button-editshop">
                                        Save
                                    </button>
                                    {suggestedCategory && (
                                        <div style={{ fontSize: '9pt', color: '#b54708', marginTop: '6px' }}>
                                            Similar category found: <b>{suggestedCategory.label}</b>. Please use this existing one.
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="form-actions-editshop">
                            <button onClick={addCard} disabled={isSaving}>
                                {isSaving ? "Saving..." : "Save"}
                            </button>
                            <button onClick={() => setShowForm(false)} style={{backgroundColor: "#555", color: "white"}}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            {popupMessage && (
                <div className="popup-message-editshop">
                    {popupMessage}
                </div>
            )}

            {editingCard && (
                <div className="form-modal-editshop">
                    <div className="form-content-editshop">
                        <div className="form-scroll-container-editshop">
                            <h3>Edit Item</h3>
                            <div className="form-row-editshop">
                                <label htmlFor="edit-name">Name</label>
                                <input id="edit-name" type="text" name="name" value={editForm.name} onChange={handleEditInputChange} />
                            </div>
                            <div className="form-row-editshop">
                                <label htmlFor="edit-description">Description</label>
                                <input id="edit-description" type="text" name="description" value={editForm.description} onChange={handleEditInputChange} />
                            </div>
                            <div className="form-row-editshop">
                                <label htmlFor="edit-mrp">MRP</label>
                                <input id="edit-mrp" type="number" name="mrp" value={editForm.mrp} onChange={handleEditInputChange} />
                            </div>
                            <div className="form-row-editshop">
                                <label htmlFor="edit-price">Selling Price</label>
                                <input id="edit-price" type="number" name="price" value={editForm.price} onChange={handleEditInputChange} />
                            </div>
                            <div className="form-row-editshop">
                                <label htmlFor="edit-discount">Discount (%)</label>
                                <input id="edit-discount" type="number" name="discount" value={editForm.discount} readOnly className="discount-input-readonly" />
                            </div>
                            <div className="form-row-editshop">
                                <label htmlFor="edit-quantity">Quantity</label>
                                <input id="edit-quantity" type="number" name="quantity" value={editForm.quantity} onChange={handleEditInputChange} />
                            </div>
                            <div className="form-row-editshop">
                                <label htmlFor="edit-image">Image (leave blank to keep existing)</label>
                                <input id="edit-image" type="file" name="image" accept="image/*" onChange={handleEditImageChange} />
                            </div>
                            <div className="form-row-editshop">
                                <label htmlFor="edit-category">Category</label>
                                <div className="select-wrapper-editshop">
                                    <Select
                                        inputId="edit-category"
                                        value={editForm.category}
                                        onChange={handleEditCategoryChange}
                                        options={categories}
                                        isSearchable
                                        placeholder="Select Category"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="form-actions-editshop">
                            <button onClick={saveEdit} disabled={isEditSaving}>
                                {isEditSaving ? "Saving..." : "Update"}
                            </button>
                            <button onClick={() => setEditingCard(null)} style={{backgroundColor: "#555", color: "white"}}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default EditShop;